// LocationPicker.tsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactSVG } from 'react-svg';
import styled from 'styled-components';
import { Building2, Plane } from 'lucide-react';
import { purpleColors, violetColors, slateColors, fontFamily } from '../ContentPages/AppLandingPage/styles';
import * as Buttons from '../Controls/Buttons';
import clearIcon from '../../icons/clear.svg';
import { Input } from '../Controls/TextFields';
import { getApi } from '../../utils/api';
import { getCountryFlag } from '../../utils/uicCountryHelper';


// Interface for props
interface LocationPickerProps {
  onClose: () => void;
  onLocationSelect: (location: { name: string; id: string }) => void;
  recentlyChosen: Array<{ name: string; id: string }>;
}

// Interface for API location
interface APILocation {
  type: string;
  id: string | null;
  name: string;
}

// Styling components
const FrameContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 90%;
  max-width: 600px;
  max-height: 90%;
  border-radius: 1.5rem;
  background: white;
  box-sizing: border-box;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  border: 1px solid ${slateColors[100]};
  padding-bottom: 1.5rem;
  
  @media (min-width: 768px) {
    border-radius: 2rem;
    padding-bottom: 2rem;
  }
`;

const TextFieldContainer = styled.div`
  display: flex;
  padding: 1.5rem 1.5rem 1rem 1.5rem;
  align-items: center;
  gap: 1rem;
  align-self: stretch;
  position: relative;
  box-sizing: border-box;
  overflow: hidden;
  
  @media (min-width: 640px) {
    padding: 2rem 2rem 1.25rem 2rem;
  }
  
  @media (min-width: 768px) {
    padding: 2.5rem 2.5rem 1.5rem 2.5rem;
  }
`

const CloseButton = styled(Buttons.TextButtonS)`
  flex-shrink: 0;
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  max-width: calc(100% - 80px);
`;

const ClearIconWrapper = styled.span<{ visible: boolean }>`
  display: ${props => (props.visible ? 'block' : 'none')};
  width: 1.25rem;
  height: 1.25rem;
  margin-left: -2.25rem;
  margin-right: 1rem;
  cursor: pointer;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    fill: ${slateColors[600]};
    transition: fill 0.2s;
  }
  
  &:hover svg {
    fill: ${slateColors[900]};
  }
`;

const ListTitle = styled.h3`
  font-family: ${fontFamily};
  display: flex;
  padding: 0 2rem 0 2.125rem;
  align-items: flex-start;
  gap: 0.625rem;
  align-self: stretch;
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${slateColors[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  
  @media (min-width: 640px) {
    padding: 0 2.5rem 0 2.625rem;
    font-size: 1rem;
  }
  
  @media (min-width: 768px) {
    padding: 0 3rem 0 3.125rem;
  }
`

const LocationList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const LocationItem = styled.li`
  font-family: ${fontFamily};
  cursor: pointer;
  display: flex;
  padding: 0.875rem 2rem 0.875rem 2.125rem;
  align-items: center;
  gap: 0.5rem;
  align-self: stretch;
  overflow: hidden;
  color: ${slateColors[800]};
  text-overflow: ellipsis;
  font-size: 1rem;
  font-weight: 500;
  transition: background-color 0.2s;
  
  @media (min-width: 640px) {
    padding: 1rem 2.5rem 1rem 2.625rem;
    font-size: 1.125rem;
    gap: 0.625rem;
  }
  
  @media (min-width: 768px) {
    padding: 1.125rem 3rem 1.125rem 3.125rem;
  }

  &:hover {
    background-color: ${slateColors[50]};
  }
`;

const LocationContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;
  
  @media (min-width: 640px) {
    gap: 0.625rem;
  }
`;

const CountryFlag = styled.span`
  font-size: 1rem;
  line-height: 1;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
`;

const FlagSpacer = styled.span`
  width: 1.3rem;
  flex-shrink: 0;
  display: block;
  
  @media (min-width: 640px) {
    width: 1.35rem;
  }
`;

const StationNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex: 1;
  min-width: 0;
  
  @media (min-width: 640px) {
    gap: 0.5rem;
  }
`;

const StationName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CityIcon = styled(Building2)`
  width: 1rem;
  height: 1rem;
  color: ${slateColors[500]};
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    width: 1.125rem;
    height: 1.125rem;
  }
`;

const AirportIcon = styled(Plane)`
  width: 1rem;
  height: 1rem;
  color: ${slateColors[500]};
  flex-shrink: 0;
  
  @media (min-width: 640px) {
    width: 1.125rem;
    height: 1.125rem;
  }
`;


const LocationPicker: React.FC<LocationPickerProps> = ({
  onClose,
  onLocationSelect,
  recentlyChosen,
}) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [suggestedLocations, setSuggestedLocations] = useState<
    Array<{ name: string; id: string }>
  >([]);
  const [isLoading, setIsLoading] = useState(false);

  const inputRef = useRef<HTMLInputElement>(null);

  // Filter out duplicates from the recentlyChosen list and only keep locations with UIC codes (7 characters)
  const uniqueRecentlyChosen = Array.from(
      new Map(recentlyChosen.map(location => [location.id, location])).values()
  ).filter(location => location.id.length === 7);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
  };

  const clearInput = () => {
    setQuery('');
    setSuggestedLocations([]);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const fetchLocations = useCallback(async (query: string) => {
    setIsLoading(true);
    try {
      const data: APILocation[] = await getApi('stations', { query });

      const stopsAndStations = data
        .filter((location) => location.type === 'stop' || location.type === 'station')
        .map((location) => ({ name: location.name, id: location.id || '' }))
        .filter((location) => location.id.length === 7); // Only keep locations with UIC codes (7 characters)
      setSuggestedLocations(stopsAndStations);
    } catch (error) {
      console.error('Error fetching locations:', error);
      setSuggestedLocations([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (query.length >= 3) {
        fetchLocations(query);
      } else {
        setSuggestedLocations([]);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [query, fetchLocations]);

    useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  // Check if the station name is entirely in uppercase (indicating it's a city name like "FRANKFURT(MAIN)")
  const isCity = (stationName: string): boolean => {
    // Check if the name contains any alphabetic characters
    const alphabeticChars = stationName.split('').filter((char: string) => /[a-zA-Z]/.test(char));
    if (alphabeticChars.length === 0) return false;
    
    // If all alphabetic characters are uppercase, it's a city name
    return alphabeticChars.every((char: string) => char === char.toUpperCase());
  };

  // Check if the station name contains the word for airport in various languages
  const isAirport = (stationName: string): boolean => {
    const lowercasedName = stationName.toLowerCase();
    
    // Airport words in different languages (case-insensitive check)
    const airportWords = [
      // English
      'airport', 'aerodrome',
      // Dutch
      'luchthaven', 'vliegveld',
      // French
      'aéroport', 'aeroport',
      // German
      'flughafen', 'flugplatz',
      // Italian
      'aeroporto',
      // Spanish
      'aeropuerto',
      // Danish
      'lufthavn',
      // Swedish
      'flygplats',
      // Polish
      'lotnisko',
      // Czech
      'letiště', 'letiste',
      // Hungarian
      'repülőtér', 'repuloter'
    ];
    
    return airportWords.some(word => lowercasedName.includes(word));
  };

  const handleLocationClick = (location: { name: string; id: string }) => {
    onLocationSelect(location);
    onClose();
  };

  // Component to render a location item with flag and city/airport icon
  const LocationItemContent: React.FC<{ location: { name: string; id: string } }> = ({ location }) => {
    const isUICCode = location.id.length === 7;
    const flag = isUICCode ? getCountryFlag(location.id) : null;
    const isCityName = isCity(location.name);
    const isAirportName = isAirport(location.name);

    return (
      <LocationContent>
        {isUICCode && flag ? (
          <CountryFlag>{flag}</CountryFlag>
        ) : (
          <FlagSpacer />
        )}
        <StationNameContainer>
          <StationName>{location.name}</StationName>
          {isAirportName && <AirportIcon />}
          {!isAirportName && isCityName && <CityIcon />}
        </StationNameContainer>
      </LocationContent>
    );
  };

  return (
    <FrameContainer>
      <TextFieldContainer>
        <InputWrapper>
          <Input
            type="text"
            placeholder={t('searchControls.chooseLocation')}
            value={query}
            onChange={handleInputChange}
            ref={inputRef}
          />
          <ClearIconWrapper visible={!!query} onClick={clearInput}>
            <ReactSVG src={clearIcon} />
          </ClearIconWrapper>
        </InputWrapper>
        <CloseButton onClick={onClose}>{t('common.cancel')}</CloseButton>
      </TextFieldContainer>
      <div style={{ height: '0.875rem' }} />

      {isLoading && (
        <div style={{ paddingBottom: '2rem' }}>
          <ListTitle>{t('common.loading')}</ListTitle>
        </div>
      )}
      
    {/* Conditional rendering for title */}
    {query.length < 3 && uniqueRecentlyChosen.length > 0 && (
      <>
        <ListTitle>{t('locationPicker.recents')}</ListTitle>
        <div style={{ height: '0.5rem' }} />
        <LocationList>
          {uniqueRecentlyChosen.map((location) => (
            <LocationItem
              key={location.id}
              onClick={() => handleLocationClick(location)}
            >
              <LocationItemContent location={location} />
            </LocationItem>
          ))}
        </LocationList>
      </>
    )}

    {query.length >= 3 && suggestedLocations.length > 0 && (
      <>
        <ListTitle>{t('locationPicker.suggested')}</ListTitle>
        <div style={{ height: '0.5rem' }} />
        <LocationList>
          {suggestedLocations.map((location) => (
            <LocationItem
              key={location.id}
              onClick={() => handleLocationClick(location)}
            >
              <LocationItemContent location={location} />
            </LocationItem>
          ))}
        </LocationList>
      </>
    )}
  </FrameContainer>
  );
};

export default LocationPicker;
